import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { getColumnsSocialSecurity } from "./columns";
import { useMemo, useRef, useState } from "react";
import { AlertDialogComponent, DataTable, Pagination } from "@/components/shared";
import AddSocialSecurityDialog from "./AddSocialSecurityDialog";
import { PaginatedSocialSecurityQuery, PaginatedSocialSecurityQueryVariables, PaginatedSocialSecurityDocument, SocialSecurity, SocialSecurityEdge } from "@/graphql/__generated__/graphql";
import useUserStore from "@/store/shield";
import { useRemoveSocialSecurityMutationHook } from "@/hooks/mutations";
import { Button } from "@/components/ui/button";
import SocialSecurityFilter from "./SocialSecurityFilter";

interface PaginationRef {
  triggerHandleFilterSubmit: (_newVariables: Partial<PaginatedSocialSecurityQueryVariables>) => void;
}

/**
 * SocialSecurityComponent component for displaying the social security table
 * @returns JSX element
 */
function SocialSecurityComponent() {
  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["components", "common"]);
  const { removeSocialSecurityMutation } = useRemoveSocialSecurityMutationHook();

  const { isConfigAdmin } = useUserStore();

  // Pagination config
  const paginationRef = useRef<PaginationRef | null>(null);
  const paginationName = "paginatedSocialSecurity";

  // State management
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveSocialSecurityOpen, setIsRemoveSocialSecurityOpen] = useState(false);
  const [selectedSocialSecurity, setSelectedSocialSecurity] = useState<SocialSecurity | null>(null);

  const onFilterSubmit = (variables: PaginatedSocialSecurityQueryVariables) => {
    // When the filter is submitted in the PageComponent,
    // this will call the handleFilterSubmit inside Pagination.
    paginationRef.current?.triggerHandleFilterSubmit(variables);
  };

  const handleRemoveClick = (socialSecurity: SocialSecurity) => {
    setSelectedSocialSecurity(socialSecurity);
    setIsRemoveSocialSecurityOpen(true);
  };

  const handleRemoveSocialSecurity = (socialSecurity: SocialSecurity) => {
    removeSocialSecurityMutation({ variables: { removeSocialSecurityId: socialSecurity.id } }).finally(() => {
      setIsRemoveSocialSecurityOpen(false);
    });
  };

  const columns = useMemo(() => getColumnsSocialSecurity(intl, t, handleRemoveClick, isConfigAdmin), [intl, t, handleRemoveClick, isConfigAdmin]);

  return (
    <>
      <Pagination<SocialSecurityEdge, PaginatedSocialSecurityQuery, PaginatedSocialSecurityQueryVariables>
        ref={paginationRef} props={{ queryDocument: PaginatedSocialSecurityDocument, paginationName }}>
        {(dataSlice, loading, displayAmount) => (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 justify-end">
              <SocialSecurityFilter onFilterSubmit={onFilterSubmit} displayAmount={displayAmount as number} />
              {isConfigAdmin && <Button
                onClick={() => setIsAddModalOpen(true)}
              >
                {t("common:buttons.add")}
              </Button>}
            </div>
            <DataTable
              loading={loading}
              displayAmount={displayAmount as number}
              data={dataSlice.map(edge => edge.node) ?? []}
              columns={columns}
              enableColumnPinning
              columnPinning={{
                right: ["actions"]
              }}
              className="max-h-[calc(100vh-20rem)]"
            />
          </div>
        )}
      </Pagination>


      <AddSocialSecurityDialog
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
      />

      {/* Remove Social Security Alert Dialog */}
      <AlertDialogComponent
        open={isRemoveSocialSecurityOpen}
        onOpenChange={setIsRemoveSocialSecurityOpen}
        actionButtonFn={() => handleRemoveSocialSecurity(selectedSocialSecurity as SocialSecurity)}
        title={t("components:alertdialogs.removetitle")}
        bodyText={t("components:alertdialogs.removesocialsecurity")}
      />
    </>
  );
}

export default SocialSecurityComponent;
